<template>
  <v-container>
    <!-- Status Filter -->
    <v-card class="mb-6">
      <div class="d-flex justify-space-between align-center ma-4">
        <v-card-text>
          <v-chip-group
            v-model="selectedStatus"
            selected-class="primary"
            column
          >
            <v-chip filter value="all">All Projects</v-chip>
            <v-chip filter value="design">Design</v-chip>
            <v-chip filter value="live">Live</v-chip>
            <v-chip filter value="complete">Complete</v-chip>
          </v-chip-group>
        </v-card-text>

        <v-switch
          v-if="isDev"
          v-model="devUnlimited"
          label="Dev: ignore limits"
          color="warning"
          density="compact"
          hide-details
          class="flex-grow-0 mr-4"
        />

        <v-btn
          v-if="canCreateProject"
          color="primary"
          prepend-icon="mdi-plus"
          to="/project/new"
        >
          New Project
        </v-btn>

        <v-btn v-else color="warning" prepend-icon="mdi-crown" to="/payment">
          Upgrade
        </v-btn>
      </div>
    </v-card>

    <!-- Projects Grid -->
    <v-row>
      <v-col v-for="project in filteredProjects" :key="project.id" cols="12">
        <v-card>
          <v-card-item>
            <template v-slot:prepend>
              <v-btn :color="getStatusColor(project.state)" variant="tonal">
                {{ project.state }}
              </v-btn>
            </template>

            <v-card-title class="text-h5">
              {{ project.name }}
            </v-card-title>

            <template v-slot:append>
              <v-btn :color="getStatusColor(project.state)" variant="tonal">
                {{ project.state }}
              </v-btn>
            </template>
          </v-card-item>

          <v-card-text>
            <p class="mb-2">{{ project.description }}</p>

            <v-row class="mt-4">
              <v-col cols="12" sm="6" md="2">
                <div class="d-flex align-center">
                  <v-icon icon="mdi-calendar" class="mr-2" />
                  <div>
                    <div class="text-caption">Created</div>
                    <div class="text-body-2">
                      {{ formatDate(project.created_at) }}
                    </div>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" sm="6" md="2">
                <div
                  class="d-flex align-center"
                  v-if="project.state === 'live'"
                >
                  <v-icon icon="mdi-calendar-clock" class="mr-2" />
                  <div>
                    <div class="text-caption">Live At</div>
                    <div class="text-body-2">
                      {{ formatDate(project.live_at) }}
                    </div>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" sm="6" md="2">
                <div
                  class="d-flex align-center"
                  v-if="project.state === 'live'"
                >
                  <v-icon icon="mdi-chart-box" class="mr-2" />
                  <div>
                    <div class="text-caption">Interviews</div>
                    <div class="text-body-2">
                      {{ project.sessions_count }}
                    </div>
                  </div>
                </div>
              </v-col>

              <!-- Replace the existing URL section with this -->
              <v-col cols="12" sm="6" md="2">
                <div
                  class="d-flex align-center"
                  v-if="project.state === 'live'"
                >
                  <v-icon icon="mdi-share-variant" class="mr-2" />
                  <div>
                    <div class="text-caption">Share</div>
                    <div class="d-flex gap-2">
                      <!-- LinkedIn -->
                      <v-btn
                        icon="mdi-linkedin"
                        size="small"
                        color="#0077b5"
                        variant="text"
                        @click="shareToLinkedIn(project._id)"
                      />
                      <!-- Facebook -->
                      <v-btn
                        icon="mdi-facebook"
                        size="small"
                        color="#1877F2"
                        variant="text"
                        @click="shareToFacebook(project._id)"
                      />
                      <!-- Twitter/X -->
                      <v-btn
                        icon="mdi-twitter"
                        size="small"
                        color="#1DA1F2"
                        variant="text"
                        @click="shareToTwitter(project._id)"
                      />
                      <!-- Copy Link -->
                      <v-btn
                        :icon="linkCopied ? 'mdi-check' : 'mdi-content-copy'"
                        size="small"
                        :color="linkCopied ? 'success' : 'grey'"
                        variant="text"
                        @click="copyLink(project._id)"
                      />
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              v-if="project.state === 'design'"
              color="primary"
              variant="tonal"
              prepend-icon="mdi-pencil"
              :to="`/project/${project._id}`"
            >
              Edit
            </v-btn>
            <v-btn
              v-if="project.state === 'live'"
              color="primary"
              variant="tonal"
              prepend-icon="mdi-chat"
              :to="`/interview/${$store.getters['tenancy/currentTenant']?.tenant_id}/${project._id}`"
            >
              Live Interview
            </v-btn>
            <v-btn
              v-if="project.state === 'complete'"
              color="primary"
              variant="tonal"
              prepend-icon="mdi-chart-bar"
              :to="`/project/${project._id}/results`"
            >
              View Results
            </v-btn>
            <v-btn
              v-if="project.state === 'design'"
              color="success"
              prepend-icon="mdi-rocket-launch"
              @click="launchProject(project._id)"
            >
              Launch
            </v-btn>
            <v-btn
              v-if="project.state === 'live'"
              color="info"
              variant="tonal"
              prepend-icon="mdi-flag-checkered"
              @click="endProject(project._id)"
            >
              End Project
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import APIService from "@/services/APIService";

export default {
  name: "DashboardView",
  data: () => ({
    selectedStatus: "all",
    projects: [],
    loading: true,
    error: null,
    linkCopied: false,
    devUnlimited:
      localStorage.getItem("dev_unlimited_projects") === "true" &&
      process.env.NODE_ENV === "development",
  }),

  computed: {
    filteredProjects() {
      if (this.selectedStatus === "all") {
        return this.projects;
      }
      return this.projects.filter(
        (project) => project.state === this.selectedStatus
      );
    },
    isDev() {
      return process.env.NODE_ENV === "development";
    },
    canCreateProject() {
      if (this.isDev && this.devUnlimited) return true;
      const isBasic = !this.$store.getters.hasActiveSubscription;
      return !(isBasic && this.projects.length >= 3);
    },
  },

  watch: {
    devUnlimited(value) {
      localStorage.setItem("dev_unlimited_projects", value ? "true" : "false");
    },
  },

  methods: {
    getStatusColor(status) {
      const colors = {
        design: "warning",
        live: "success",
        complete: "info",
      };
      return colors[status] || "grey";
    },

    formatDate(dateString) {
      if (!dateString) return "";
      return new Date(dateString).toLocaleDateString();
    },

    async fetchProjects() {
      this.loading = true;
      this.error = null;
      try {
        const [projectsRes, interviewsRes] = await Promise.all([
          APIService.getRecords("di_projects"),
          APIService.getRecords("di_interviews"),
        ]);

        this.projects = projectsRes.data.records;

        if (this.projects.length === 0) {
          this.$router.push("/project/new");
          return;
        }

        // Count interviews per project
        const interviewCounts = {};
        for (const interview of interviewsRes.data.records || []) {
          const pid = interview.project_id;
          interviewCounts[pid] = (interviewCounts[pid] || 0) + 1;
        }

        this.projects.forEach((project) => {
          project.interview_count = interviewCounts[project._id] || 0;
        });
      } catch (error) {
        console.error("Error fetching projects:", error);
        this.error = "Failed to load projects. Please try again later.";
      } finally {
        this.loading = false;
      }
    },

    async launchProject(projectId) {
      try {
        await APIService.updateRecord("di_projects", projectId, {
          state: "live",
          live_at: new Date().toISOString(),
        });
        this.fetchProjects();
      } catch (error) {
        console.error("Error launching project:", error);
        alert("Failed to launch project. Please try again later.");
      }
    },

    async endProject(projectId) {
      const confirmed = confirm(
        "End this project? The interview link will close and a final analysis will run."
      );
      if (!confirmed) return;
      try {
        await APIService.updateRecord("di_projects", projectId, {
          state: "complete",
          end_at: new Date().toISOString(),
        });
        this.fetchProjects();
      } catch (error) {
        console.error("Error ending project:", error);
        alert("Failed to end project. Please try again later.");
      }
    },

    getShareUrl(projectId) {
      const tenantId =
        this.$store.getters["tenancy/currentTenant"]?.tenant_id || "";
      return `${window.location.origin}${window.location.pathname}#/interview/${tenantId}/${projectId}`;
    },

    shareToLinkedIn(projectId) {
      const project = this.projects.find((p) => p.project_id === projectId);
      const url = encodeURIComponent(this.getShareUrl(projectId));
      const title = encodeURIComponent(project.name);
      const summary = encodeURIComponent(project.description);
      window.open(
        `https://www.linkedin.com/sharing/share-offsite/?url=${url}&title=${title}&summary=${summary}`,
        "_blank"
      );
    },

    shareToFacebook(projectId) {
      const project = this.projects.find((p) => p.project_id === projectId);
      const url = encodeURIComponent(this.getShareUrl(projectId));
      const quote = encodeURIComponent(
        `${project.name}: ${project.description}`
      );
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`,
        "_blank"
      );
    },

    shareToTwitter(projectId) {
      const project = this.projects.find((p) => p.project_id === projectId);
      const url = encodeURIComponent(this.getShareUrl(projectId));
      const text = encodeURIComponent(
        `Check out ${project.name}: ${project.description}`
      );
      window.open(
        `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
        "_blank"
      );
    },

    async copyLink(projectId) {
      const url = this.getShareUrl(projectId);
      try {
        await navigator.clipboard.writeText(url);
        this.linkCopied = true;
        setTimeout(() => {
          this.linkCopied = false;
        }, 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
      }
    },
  },

  created() {
    this.fetchProjects();
  },
};
</script>
